import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Check, X, Search, Target, Rocket, Building2 } from 'lucide-react';
import { createCheckoutSession, getSubscriptionStatus, getPriceId } from '../lib/stripe';
import AuroraBackground from '../components/ui/AuroraBackground';
import toast from 'react-hot-toast';
import '../styles/aurora-effects.css';

interface Plan {
  name: string;
  tier: 'scout' | 'hunter' | 'pro' | 'business';
  price: {
    monthly: number | string;
    annual: number | string;
  };
  features: string[];
  notIncluded?: string[];
  recommended?: boolean;
  description: string;
  icon: any;
  stripePriceIds?: {
    monthly: string | null;
    annual: string | null;
  };
}

const plans: Plan[] = [
  {
    name: 'Scout',
    tier: 'scout',
    price: {
      monthly: '$0',
      annual: '$0',
    },
    description: 'Perfect for casual fans who track a few favorite events',
    icon: Search,
    features: [
      '3 active price alerts',
      'Email notifications only',
      '48-hour delay on deal alerts',
      'Basic search (all events/venues)',
      'Price history graph (last 7 days)',
      'Mobile web access',
      '1 device login',
    ],
    notIncluded: [
      'SMS or push notifications',
      'Price predictions',
      'Group coordination tools',
      'Presale code access',
      'API access',
      'Priority customer support',
    ],
    stripePriceIds: {
      monthly: null,
      annual: null,
    },
  },
  {
    name: 'Hunter',
    tier: 'hunter',
    price: {
      monthly: 9.99,
      annual: 99,
    },
    description: 'For deal seekers who never want to miss a price drop',
    icon: Target,
    features: [
      '20 active price alerts',
      'Real-time notifications',
      'SMS alerts & push notifications',
      'Custom alert triggers',
      'Price predictions (7 days)',
      'Deal Score algorithm (1-100)',
      'Price history (30 days)',
      'Venue heat maps',
      'Best time to buy recommendations',
      'Multi-device sync (up to 3)',
      'Alert history (90 days)',
      'Monthly savings report',
      'Email priority support (24hr)',
      'No ads',
      'Flash deal alerts',
      'Competitor price tracking (5 platforms)',
      'Wishlist sharing',
    ],
    notIncluded: [
      'Unlimited alerts',
      'Squad Mode',
      'Presale codes',
      'API access',
    ],
    recommended: true,
    stripePriceIds: {
      monthly: 'price_1RzWG20GdyORS8iwyYKkMfgV',
      annual: 'price_1RzWG30GdyORS8iwVB9JqLMN',
    },
  },
  {
    name: 'Pro',
    tier: 'pro',
    price: {
      monthly: 24.99,
      annual: 249,
    },
    description: 'For power users attending multiple events monthly',
    icon: Rocket,
    features: [
      'Unlimited active alerts',
      'Priority notifications (30s head start)',
      'Complex alert rules',
      'Bulk alert management',
      'Alert folders & organization',
      'Geofencing alerts',
      'Squad Mode (up to 10 people)',
      'Seat block finder',
      'Group chat',
      'Split payment calculator',
      'Social alerts',
      'Advanced price predictions (14 days)',
      'Seller analytics',
      'Personal analytics dashboard',
      'Market trends',
      'Inventory tracking',
      'Presale code aggregator (20+ sources)',
      'VIP deal access',
      'Beta features',
      'API access (1,000 calls/month)',
      'Data export (CSV)',
      'Priority phone support',
      'Unlimited devices',
      '180-day alert history',
    ],
    stripePriceIds: {
      monthly: 'price_1RzWG40GdyORS8iwkgDSvca1',
      annual: 'price_1RzWG40GdyORS8iw2CfsWp5t',
    },
  },
  {
    name: 'Business',
    tier: 'business',
    price: {
      monthly: 99.99,
      annual: 999,
    },
    description: 'For small resellers and professional buyers',
    icon: Building2,
    features: [
      'Everything in Pro',
      'Bulk operations dashboard',
      'Advanced API access (50,000 calls/month)',
      'Webhook support',
      'Custom integrations (Zapier, Slack, Discord)',
      'White-label options',
      'Sub-accounts (up to 5 team members)',
      'ROI calculator',
      'Arbitrage alerts',
      'Competitor intelligence',
      'Custom reports',
      'Market maker tools',
      'Price elasticity models',
      'Auto-buy rules (requires approval)',
      'Virtual assistant (AI recommendations)',
      'Portfolio management',
      'Tax reporting',
      'Profit/Loss tracking',
      'Multi-currency support',
      '24/7 phone support',
      'Dedicated success manager',
      'Custom training',
      'SLA guarantee (99.9% uptime)',
      'Priority feature requests',
      'Unlimited alert history',
      'Broker network access',
      'Industry reports',
      'Legal compliance tools',
      'Insurance partnership',
    ],
    stripePriceIds: {
      monthly: 'price_1RzWG50GdyORS8iwNudMeWFU',
      annual: 'price_1RzWG50GdyORS8iwvRZMFvzg',
    },
  },
];

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [loading, setLoading] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Fetch current subscription status
    loadSubscriptionStatus();
  }, [user, navigate]);

  const loadSubscriptionStatus = async () => {
    try {
      const status = await getSubscriptionStatus();
      setCurrentSubscription(status);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const handleSubscribe = async (plan: Plan) => {
    if (!user) {
      toast.error('Please sign in to subscribe');
      navigate('/login');
      return;
    }

    // Handle free Scout plan
    if (plan.tier === 'scout') {
      navigate('/dashboard');
      toast.success('Welcome to Scout! Start tracking events for free.');
      return;
    }

    setLoading(plan.tier);
    
    try {
      const priceId = plan.stripePriceIds?.[billingPeriod];
      
      if (!priceId) {
        throw new Error('Invalid price configuration');
      }

      // Direct payment links mapping for each price ID
      const paymentLinks: Record<string, string> = {
        // Monthly plans
        'price_1RzWG20GdyORS8iwyYKkMfgV': 'https://buy.stripe.com/test_3cIbIT1TicomcgBckhgMw02', // Hunter monthly
        'price_1RzWG40GdyORS8iwkgDSvca1': 'https://buy.stripe.com/test_14A5kvapO5ZY1BXfwtgMw04', // Pro monthly
        'price_1RzWG50GdyORS8iwNudMeWFU': 'https://buy.stripe.com/test_cNibIT7dCbkicgB5VTgMw06', // Enterprise monthly
        // Annual plans
        'price_1RzWG30GdyORS8iwVB9JqLMN': 'https://buy.stripe.com/test_bJe00b7dCfAy4O9985gMw03', // Hunter annual
        'price_1RzWG40GdyORS8iw2CfsWp5t': 'https://buy.stripe.com/test_00wfZ99lK1JI4O93NLgMw05', // Pro annual
        'price_1RzWG50GdyORS8iwvRZMFvzg': 'https://buy.stripe.com/test_dRm8wHbtSewueoJdolgMw07', // Enterprise annual
      };

      const paymentLink = paymentLinks[priceId];
      
      if (!paymentLink) {
        // Fallback to Edge Function approach if payment link not found
        await createCheckoutSession({
          priceId,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription`,
        });
        return;
      }

      // Add user email to the payment link if available
      const url = new URL(paymentLink);
      if (user.email) {
        url.searchParams.set('prefilled_email', user.email);
      }
      
      // Redirect to Stripe payment link
      window.location.href = url.toString();
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to start subscription process');
      setLoading(null);
    }
  };

  const formatPrice = (price: number | string) => {
    if (typeof price === 'string') return price;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    }).format(price);
  };

  const calculateSavings = (monthly: number | string, annual: number | string) => {
    if (typeof monthly === 'string' || typeof annual === 'string') {
      return { savings: 0, percentage: 0 };
    }
    const monthlyCost = monthly * 12;
    const savings = monthlyCost - annual;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return { savings, percentage };
  };

  return (
    <AuroraBackground>
      <div className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-white mb-4">
              Choose Your{' '}
              <span className="bg-gradient-to-r from-teal-400 via-aurora-green to-emerald-400 bg-clip-text text-transparent">
                Subscription
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Get instant alerts when ticket prices drop for your favorite events
            </p>

            {/* Current Subscription Status */}
            {currentSubscription && currentSubscription.subscription_status === 'active' && (
              <div className="bg-teal-500/20 border border-teal-400/50 rounded-lg p-4 mb-8 max-w-md mx-auto backdrop-blur-sm">
                <p className="text-teal-300">
                  You're currently on the <strong className="text-white">{currentSubscription.subscription_tier}</strong> plan
                </p>
                <button
                  onClick={() => navigate('/subscription/manage')}
                  className="mt-2 text-teal-400 hover:text-teal-300 underline"
                >
                  Manage subscription →
                </button>
              </div>
            )}

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className={`text-lg ${billingPeriod === 'monthly' ? 'font-semibold text-white' : 'text-gray-400'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
                className="relative inline-flex h-8 w-14 items-center rounded-full bg-hawk-dark/70 border border-teal-400/30 transition-colors hover:border-teal-400/50"
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-gradient-to-r from-teal-500 to-aurora-green shadow-lg transition-transform ${
                    billingPeriod === 'annual' ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-lg ${billingPeriod === 'annual' ? 'font-semibold text-white' : 'text-gray-400'}`}>
                Annual
                <span className="ml-2 text-sm text-teal-400 font-medium">Save 20%</span>
              </span>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => {
              const price = billingPeriod === 'monthly' ? plan.price.monthly : plan.price.annual;
              const { savings, percentage } = calculateSavings(plan.price.monthly, plan.price.annual);
              const isCurrentPlan = currentSubscription?.subscription_tier === plan.tier.toLowerCase();

              return (
                <div
                  key={plan.tier}
                  className={`relative bg-hawk-dark/70 backdrop-blur-sm rounded-2xl border overflow-hidden transition-all duration-300 transform hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(20,184,166,0.3)] ${
                    plan.recommended 
                      ? 'border-teal-400 hover:border-teal-300' 
                      : 'border-hawk-dark-accent/30 hover:border-teal-400'
                  }`}
                >
                  {plan.recommended && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-teal-500 to-aurora-green text-white px-4 py-1 rounded-bl-lg z-10">
                      <span className="text-sm font-semibold">MOST POPULAR</span>
                    </div>
                  )}

                  <div className="p-6">
                    {/* Plan Icon */}
                    <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-teal-500 to-aurora-green rounded-xl mb-4 shadow-lg">
                      <plan.icon className="w-7 h-7 text-white" />
                    </div>

                    {/* Plan Name */}
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-gray-400 mb-6">{plan.description}</p>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold bg-gradient-to-r from-teal-400 to-aurora-green bg-clip-text text-transparent">
                          {typeof price === 'string' ? price : formatPrice(price)}
                        </span>
                        {typeof price !== 'string' && (
                          <span className="ml-2 text-gray-400">
                            /{billingPeriod === 'monthly' ? 'month' : 'year'}
                          </span>
                        )}
                      </div>
                      {billingPeriod === 'annual' && percentage > 0 && (
                        <p className="text-sm text-teal-400 mt-1">
                          Save {formatPrice(savings)} ({percentage}% off)
                        </p>
                      )}
                    </div>

                    {/* CTA Button */}
                    {isCurrentPlan ? (
                      <button
                        disabled
                        className="w-full py-3 px-6 bg-hawk-dark/50 text-gray-500 rounded-lg font-semibold cursor-not-allowed border border-hawk-dark-accent/30"
                      >
                        Current Plan
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (plan.tier === 'business' && plan.price.monthly === 'Custom') {
                            window.location.href = 'mailto:sales@tickethauk.com';
                          } else {
                            handleSubscribe(plan);
                          }
                        }}
                        disabled={loading === plan.tier}
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                          plan.recommended
                            ? 'bg-gradient-to-r from-teal-500 to-aurora-green text-white hover:from-teal-400 hover:to-aurora-green/80 shadow-lg hover:shadow-xl'
                            : 'bg-hawk-dark/50 border border-teal-400/30 text-white hover:bg-hawk-dark/70 hover:border-teal-400/50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {loading === plan.tier ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          plan.tier === 'scout' ? 'Start Free' : 
                          plan.tier === 'business' && plan.price.monthly === 'Custom' ? 'Contact Sales' : 'Get Started'
                        )}
                      </button>
                    )}

                    {/* Features */}
                    <div className="mt-8 space-y-4">
                      <h4 className="font-semibold text-white">What's included:</h4>
                      <ul className="space-y-3">
                        {plan.features.map((feature) => (
                          <li key={feature} className="flex items-start">
                            <Check className="w-5 h-5 text-teal-400 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-300">{feature}</span>
                          </li>
                        ))}
                        {plan.notIncluded?.map((feature) => (
                          <li key={feature} className="flex items-start opacity-50">
                            <X className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-500">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* API Strategy Info */}
          <div className="mt-12 bg-hawk-dark/50 backdrop-blur-sm rounded-xl border border-teal-400/30 p-6">
            <h3 className="text-xl font-bold text-white mb-4 text-center">
              🚀 How We Maximize Limited API Calls
            </h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-teal-400">95%+</p>
                <p className="text-sm text-gray-400">Cache Hit Rate</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-400">&lt;100ms</p>
                <p className="text-sm text-gray-400">Response Time</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-400">10K+</p>
                <p className="text-sm text-gray-400">Active Users</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-yellow-400">5,000</p>
                <p className="text-sm text-gray-400">Daily API Calls</p>
              </div>
            </div>
            <p className="text-center text-gray-400 mt-4 text-sm">
              Our intelligent caching system serves thousands of users with just 5,000 API calls per day,
              ensuring fast response times and fresh data based on your subscription tier.
            </p>
          </div>

          {/* Bottom CTAs */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 mb-4">
              Free tier available forever. Paid plans include 14-day free trial.
            </p>
            <div className="flex items-center justify-center gap-8">
              <button
                onClick={() => navigate('/subscription/manage')}
                className="text-teal-400 hover:text-teal-300 underline"
              >
                Manage existing subscription
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-400 hover:text-gray-300 underline"
              >
                Back to dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </AuroraBackground>
  );
}